import React, { useState, useCallback, useRef, useMemo } from 'react';
import { 
  Maximize2, 
  Grid3X3, 
  Camera,
  Projector
} from 'lucide-react';
import { Button } from '../ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import BabylonScene from './BabylonScene';
import ViewCube from './ViewCube';
import { useApp, ASPECT_RATIOS } from '../../context/AppContext';

const Viewport = () => {
  const { 
    sceneObjects, 
    projectors, 
    selectedObjectId, 
    gizmoMode,
    controlMode,
    editMode,
    viewPreset: globalViewPreset,
    setViewPreset: setGlobalViewPreset,
    worldSettings,
    toggleGrid,
    captureScreenshotRef
  } = useApp();

  // Get selected projector info
  const selectedProjector = useMemo(() => {
    return projectors.find(p => p.id === selectedObjectId);
  }, [projectors, selectedObjectId]);

  // Calculate lux (with stacking multiplier — virtual brightness boost)
  const calculateLux = useCallback((proj) => {
    if (!proj || !proj.power_on) return 0;
    const baseLumens = proj.spec?.brightness || proj.lumens || 10000;
    const s = proj.stacking;
    const stackCount = (s && s.enabled)
      ? 1 + (parseInt(s.top) || 0) + (parseInt(s.bottom) || 0) + (parseInt(s.left) || 0) + (parseInt(s.right) || 0)
      : 1;
    const lumens = baseLumens * stackCount;
    const distance = proj.throw_distance || 5;
    const aspectRatio = ASPECT_RATIOS[proj.aspect_ratio] || ASPECT_RATIOS['16:10'];
    const projWidth = distance / proj.throw_ratio;
    const projHeight = projWidth / aspectRatio.value;
    const area = projWidth * projHeight;
    const lux = (lumens * (proj.brightness / 100)) / area;
    return Math.round(lux);
  }, []);

  const [viewMode, setViewMode] = useState('perspective');
  const [viewPreset, setViewPreset] = useState(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Listen for native fullscreen change so the icon state stays in sync if
  // the user presses Esc to exit fullscreen.
  React.useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Sync global viewPreset (from MenuBar) to local state
  React.useEffect(() => {
    if (globalViewPreset) {
      if (globalViewPreset === 'reset') {
        setViewMode('perspective');
      } else if (typeof globalViewPreset === 'object') {
        // Isometric / saved-camera views are perspective; keep the dropdown label consistent.
        setViewMode('perspective');
      } else {
        setViewMode(globalViewPreset);
      }
      setViewPreset(globalViewPreset);
      setGlobalViewPreset(null); // Clear global so it can be set again
    }
  }, [globalViewPreset, setGlobalViewPreset]);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    if (mode === 'perspective') {
      setViewPreset('reset');
    } else {
      setViewPreset(mode);
    }
  }, []);

  const handleViewPresetReset = useCallback(() => {
    setViewPreset(null);
  }, []);

  const handleScreenshot = useCallback(async () => {
    const capture = captureScreenshotRef?.current;
    if (!capture) {
      return;
    }
    // Use the canvas's current display size for a 1:1 screenshot of what
    // the user sees. CreateScreenshotUsingRenderTarget gives a clean frame
    // without the React-overlay UI (HUD, dropdowns, etc.).
    const canvas = document.querySelector('canvas');
    const w = canvas?.width || 1920;
    const h = canvas?.height || 1080;
    const dataUrl = await capture(w, h);
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.download = `iav-screenshot-${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
  }, [captureScreenshotRef]);

  const handleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (req) req.call(el);
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
      if (exit) exit.call(document);
    }
  }, []);

  const totalObjects = sceneObjects.length + projectors.length;
  const totalVertices = sceneObjects.reduce((sum, obj) => {
    if (obj.type === 'primitive') {
      switch (obj.primitive_type) {
        case 'box': return sum + 24;
        case 'sphere': return sum + 1024;
        case 'plane': return sum + 4;
        case 'cylinder': return sum + 96;
        default: return sum + 24;
      }
    }
    return sum;
  }, 0);

  return (
    <div ref={containerRef} className="viewport-container" data-testid="viewport-container">
      <BabylonScene 
        transformMode={gizmoMode}
        viewPreset={viewPreset}
        onViewPresetReset={handleViewPresetReset}
        controlMode={controlMode}
      />

      {/* Interactive 3D ViewCube — top-right of viewport, click faces/corners to change view. */}
      <ViewCube />
      
      {/* Selected Projector Info - Top Center */}
      {selectedProjector && !editMode && (
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10" data-testid="projector-info-bar">
          <div className="bg-zinc-900/90 backdrop-blur-sm border border-zinc-700 rounded-lg px-4 py-2 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Projector className={`w-4 h-4 ${selectedProjector.power_on ? 'text-yellow-400' : 'text-zinc-500'}`} />
              <span className="text-sm font-medium text-white">{selectedProjector.name}</span>
            </div>
            <div className="h-4 w-px bg-zinc-600" />
            <div className="flex items-center gap-3 text-xs">
              <span className="text-zinc-400">
                <span className="text-emerald-400 font-mono">{calculateLux(selectedProjector).toLocaleString()}</span> lx
              </span>
              <span className="text-zinc-400">
                tr: <span className="text-cyan-400 font-mono">{selectedProjector.throw_ratio.toFixed(2)}</span>
              </span>
              <span className="text-zinc-400">
                <span className="text-purple-400 font-mono">{selectedProjector.aspect_ratio}</span>
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Viewport Controls */}
      <div className="viewport-controls">
        <TooltipProvider>
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="secondary" 
                    size="sm"
                    className="bg-zinc-800/90 hover:bg-zinc-700 text-xs h-8"
                    data-testid="view-mode-dropdown"
                  >
                    {viewMode === 'perspective' ? 'Perspective' : viewMode.charAt(0).toUpperCase() + viewMode.slice(1) + ' (Ortho)'}
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>View Mode</TooltipContent>
            </Tooltip>
            <DropdownMenuContent className="bg-zinc-800 border-zinc-700">
              <DropdownMenuItem onClick={() => handleViewModeChange('perspective')} className="text-xs">
                Perspective
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleViewModeChange('top')} className="text-xs">
                Top
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleViewModeChange('bottom')} className="text-xs">
                Bottom
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleViewModeChange('front')} className="text-xs">
                Front
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleViewModeChange('back')} className="text-xs">
                Back
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleViewModeChange('left')} className="text-xs">
                Left
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleViewModeChange('right')} className="text-xs">
                Right
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className={`h-8 w-8 ${worldSettings?.showGrid ? 'text-fuchsia-400' : 'text-zinc-400'}`}
                onClick={toggleGrid}
                data-testid="toggle-grid-btn"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle Grid ({worldSettings?.showGrid ? 'ON' : 'OFF'})</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8 text-zinc-400 hover:text-white"
                onClick={handleScreenshot}
                data-testid="screenshot-btn"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Take Screenshot</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className={`h-8 w-8 ${isFullscreen ? 'text-fuchsia-400' : 'text-zinc-400 hover:text-white'}`}
                onClick={handleFullscreen}
                data-testid="fullscreen-btn"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Viewport Info */}
      <div className="viewport-info" data-testid="viewport-info">
        <span>objects: {totalObjects}</span>
        <span className="ml-4">vertices: {totalVertices.toLocaleString()}</span>
      </div>
    </div>
  );
};

export default Viewport;
